import {HOMEPAGE_PATH} from 'nav/sidepanel/homepage/HomepageConstants';
import {LegacyUrlPatternPrecedence} from 'routing/LegacyUrlPatternPrecedence';

/**
 * How to add your product area to Amplitude element autocapture
 *
 * 1. Append an object to {@link AMPLITUDE_PRODUCT_AREA_ALLOWLISTS} below.
 * 2. Set `id` to a stable slug (e.g. `'registry'`).
 * 3. List `urlPatterns` in the same shape as your RouteBundle / legacy URL
 *    patterns (e.g. `'samples/plates/:plateId'`). Prefer copying from your
 *    route files rather than inventing new paths.
 * 4. Leave `maskReadableText` unset (defaults to `true`) for a cautious
 *    rollout. Set `maskReadableText: false` only when readable UI labels are
 *    approved for your area. `amplitudePrivacy.ts` reads this flag when
 *    choosing redaction level.
 * 5. Do not edit sanitization logic in `amplitudePrivacy.ts` for allowlist-only
 *    changes.
 */

export interface AmplitudeProductAreaAllowlist {
  id: string;
  /**
   * Route urlPatterns in the same shape as RouteBundle / LegacyUrlPatternPrecedence
   * (e.g. 'chats/:id', 'samples/plates/:plateId').
   */
  urlPatterns: ReadonlyArray<string>;
  /**
   * When true (default), paths matching these patterns use MASK_READABLE_TEXT
   * (UI labels replaced with a fixed mask). Set false to keep readable labels
   * once a product area is ready to expose them (SANITIZE_URLS_ONLY).
   */
  maskReadableText?: boolean;
}

/** Product areas opted into Amplitude element autocapture. */
export const AMPLITUDE_PRODUCT_AREA_ALLOWLISTS: ReadonlyArray<AmplitudeProductAreaAllowlist> = [
  {
    id: 'insights',
    urlPatterns: LegacyUrlPatternPrecedence.filter(
      (pattern) => pattern === 'analytics' || pattern.startsWith('analytics/')
    ),
  },
  {
    id: 'model-hub',
    urlPatterns: [
      'model-hub',
      'models',
      'new-prediction',
      'predictions',
      'predictions/:id',
      'prediction-batches',
      'prediction-batches/:id',
    ],
  },
  {
    id: 'ai-chat',
    // Matches {@link llm/routes/AIRoutes} chat and settings urlPatterns, plus surfaces
    // where users can send or open chat (ShareBar Ask, entry AI, templates).
    urlPatterns: [
      'chats/new',
      'chats/:id',
      'chats/settings',
      'chats/settings/general',
      'chats/settings/skills',
      'chats/settings/skills/:itemId',
      'chats/settings/agents',
      'chats/settings/agents/:itemId',
      'chats/settings/connectors',
      'chats/settings/connectors/:itemId',
      'ai-connectors',
      's/:apiIdentifier/edit',
      ':currentUserUrl/f/:folderIdentifier/:itemUrlIdentifier/edit',
      'samples/containers/:containerId',
      'samples/boxes/:boxId',
      'samples/plates/:plateId',
      'samples/locations/:locationId',
      'samples/locations/:locationId/edit',
      ':currentUserUrl/template-collections/',
      ':currentUserUrl/template-collections/:collectionId',
    ],
  },
  {
    id: 'lab-automation',
    urlPatterns: [
      'instrument-gateways',
      'connections/listing',
      'connections/:connectionId',
      'assay-schemas/runs/',
      'assay-schemas/runs/create',
      'assay-schemas/runs/:assaySchemaId',
      'assay-schemas/runs/json/create',
      'assay-schemas/runs/json/:assaySchemaId',
      ':currentUserUrl/assay-schemas/runs/',
      ':currentUserUrl/assay-schemas/runs/create',
      ':currentUserUrl/assay-schemas/runs/:assaySchemaId',
      ':currentUserUrl/assay-schemas/runs/json/create',
      ':currentUserUrl/assay-schemas/runs/json/:assaySchemaId',
      'assay-runs/:runId',
    ],
  },
  {
    id: 'homepage',
    urlPatterns: [HOMEPAGE_PATH.slice(1)],
  },
];

/** Convert RouteBundle-style urlPatterns to Amplitude pageUrlAllowlist regexes. */
export function toAmplitudePageUrlAllowlistRegexes(urlPatterns: ReadonlyArray<string>): Array<RegExp> {
  return urlPatterns.map((urlPattern) => {
    const pathPattern = urlPattern.replace(/:[^/]+/g, '[^/]+');
    // Anchor at pathname start or absolute HTTP(S) origin so allowlisted suffixes
    // (e.g. `/admin/predictions/...`, `/foo/models`) do not match.
    return new RegExp(`^(?:https?:\\/\\/[^/?#]+)?/${pathPattern}(?:/|$|\\?|#)`);
  });
}

/** Page URL patterns where Amplitude element autocapture is enabled during initial rollout. */
export const AMPLITUDE_PAGE_URL_ALLOWLIST: Array<RegExp> = AMPLITUDE_PRODUCT_AREA_ALLOWLISTS.flatMap((area) =>
  toAmplitudePageUrlAllowlistRegexes(area.urlPatterns)
);
